import type {IOnDestroy, IScope} from 'angular';
import {transferify} from 'shared/utils/data-service-utils';
import type {ModalWindowActions} from '../conversation';
import KpUniversalFormForceSettingsWrapper
    from '../../value-editors/kp-value-editor-force-settings/KpUniversalFormForceSettingsWrapper.svelte';
import type {ForceSetting} from '../../value-editors/kp-value-editor-force-settings/types';
import type {FormController} from '../../value-editors/internal/forms/types';
import type { KpUniversalFormSettings } from 'src/commons/value-editors/kp-universal-form/types';
import type {Subscription} from 'rxjs';
import {isEqual} from 'lodash';

export interface UniversalFormModalModel {
    formSetting: KpUniversalFormSettings<Record<string, unknown>>;
    formObject: Record<string, unknown>;
    submitFunction: (...args: any[]) => any;
    customTransferifyFunction?: (...args: any[]) => any;
}

/**
 * @ngdoc controller
 * @name UniversalFormController
 * @module portaro.conversation
 *
 * @description
 *
 */
export default class UniversalFormController implements IOnDestroy {
    public static readonly controllerName = 'UniversalFormController';

    public kpUniversalFormForceSettingsWrapper = KpUniversalFormForceSettingsWrapper;

    public formObject: Record<string, unknown>; // template access (ng-input expression)
    public formSetting: KpUniversalFormSettings<Record<string, unknown>>; // template access
    public formSubmitted = false

    private kpUniversalFormForceSettingsWrapperProps = {};
    private formController: FormController<Record<string, any>>; // passed from kp-universal-form
    private formStateSubscription: Subscription;
    private asyncValidationPendingSubscription: Subscription;
    private isFormValid: boolean;
    private isAsyncValidationPending: boolean;
    private transferifyFunction: (...args: any[]) => any;
    private submitFunction: (...args: any[]) => any;
    private submitPromiseFunction: ModalWindowActions['submitPromise'];

    /*@ngInject*/
    constructor(private $scope: IScope) {
    }

    public init = (formSetting: KpUniversalFormSettings<Record<string, unknown>>,
                   formObject: Record<string, unknown>,
                   submitFunction: (...args: any[]) => any,
                   submitPromiseFunction: ModalWindowActions['submitPromise'],
                   transferifyFunction: (...args: any[]) => any = (obj) => transferify(obj)) => {
        this.formSetting = formSetting;
        this.formObject = formObject;
        this.submitFunction = submitFunction;
        this.submitPromiseFunction = submitPromiseFunction;
        this.transferifyFunction = transferifyFunction;
        this.formController = null;
    }

    public $onDestroy(): void {
        this.formStateSubscription?.unsubscribe();
        this.asyncValidationPendingSubscription?.unsubscribe();
    }

    // TODO: remove this method when refactoring to svelte
    // this is hack to make svelte-angular data binding work in angular
    // if new props object is created every time then it crashes angular due to the digest cycle loop
    // if props is const object and its properties are mutated then angular does not detect changes
    // => we need to detect changes ourself and cache most current props
    public getProps = (newProps) => {
        if (!isEqual(this.kpUniversalFormForceSettingsWrapperProps, newProps)) {
            this.kpUniversalFormForceSettingsWrapperProps = newProps;
        }
        return this.kpUniversalFormForceSettingsWrapperProps;
    };

    public submit() {
        if (!this.formSubmitted) {
            this.formSubmitted = true;
        }

        if (this.isFormValid && !this.isAsyncValidationPending) {
            const transferableFormObject = this.transferifyFunction(this.formObject);
            this.submitPromiseFunction(this.submitFunction(transferableFormObject));
        }
    }

    public refreshScope() {
        this.$scope.$applyAsync();
    }

    public getFormController(formController: FormController<Record<string, any>>) {
        this.formController = formController;
        this.formStateSubscription = this.formController.getFieldState$().subscribe((state) => {
            this.isFormValid = state.valid;
            this.refreshScope();
        });
        this.asyncValidationPendingSubscription = this.formController.isAsyncValidationPending().subscribe((isAsyncValidationPending) => {
            this.isAsyncValidationPending = isAsyncValidationPending;
            this.refreshScope();
        })
    }

    public createUniversalFormForceSettings(formSettings: KpUniversalFormSettings<Record<string, unknown>>): ForceSetting[] {
        if (this.hasAllBooleanFields(formSettings)) {
            return [{type: 'object', options: {labelsWidth: 6}}]
        }
        return [];
    }

    private hasAllBooleanFields(formSettings: KpUniversalFormSettings<Record<string, unknown>>): boolean {
        return formSettings.fields.every((field) => field.editor.type === 'boolean');
    }
}
